/**
 * Dungeon state — what room are we in, what phase is the boss on, what
 * mechanic is winding up. The Babylon scene reads this to render
 * telegraph rings/cones; the HUD shows boss HP + mechanic warnings.
 */

import { create } from 'zustand';

export type DungeonStatus = 'waiting' | 'engaged' | 'wiped' | 'cleared';

export interface DungeonMechanic {
  id: string;
  origin: { x: number; z: number };
  radius: number;
  firesAt: number; // server ms
  damage: number;
  phaseIdx: number;
}

/** R232q5aJ: open loot-roll for the local player to vote on. */
export interface DungeonLootRoll {
  id: string;
  itemId: string;
  bossId: string;
  startedAt: number;
  endsAt: number;
  myChoice: '' | 'need' | 'greed' | 'pass';
  resolved?: { winnerName: string; winnerRoll: number };
}

interface DungeonStore {
  active: boolean;
  dungeonId: string;
  mode: 'normal' | 'heroic';
  status: DungeonStatus;
  bossId: string;
  phaseIdx: number;
  mechanics: DungeonMechanic[];
  loot: string[];
  rolls: DungeonLootRoll[];
  setActive: (a: boolean) => void;
  setDungeonId: (id: string) => void;
  setMode: (m: 'normal' | 'heroic') => void;
  setStatus: (s: DungeonStatus) => void;
  setBossId: (id: string) => void;
  setPhaseIdx: (i: number) => void;
  addMechanic: (m: DungeonMechanic) => void;
  removeMechanic: (firesAt: number) => void;
  pushLoot: (itemId: string) => void;
  pushRoll: (r: Omit<DungeonLootRoll, 'myChoice'>) => void;
  setMyChoice: (rollId: string, choice: 'need' | 'greed' | 'pass') => void;
  resolveRoll: (rollId: string, winnerName: string, winnerRoll: number) => void;
  dropRoll: (rollId: string) => void;
  reset: () => void;
}

export const useDungeon = create<DungeonStore>((set, get) => ({
  active: false,
  dungeonId: '',
  mode: 'normal',
  status: 'waiting',
  bossId: '',
  phaseIdx: 0,
  mechanics: [],
  loot: [],
  rolls: [],
  setActive: (a) => set({ active: a }),
  setDungeonId: (id) => set({ dungeonId: id }),
  setMode: (m) => set({ mode: m }),
  setStatus: (s) => set({ status: s }),
  setBossId: (id) => set({ bossId: id }),
  setPhaseIdx: (i) => set({ phaseIdx: i }),
  addMechanic: (m) => set({ mechanics: [...get().mechanics, m] }),
  removeMechanic: (firesAt) => set({ mechanics: get().mechanics.filter((m) => m.firesAt !== firesAt) }),
  pushLoot: (itemId) => set({ loot: [...get().loot, itemId] }),
  pushRoll: (r) => set({ rolls: [...get().rolls, { ...r, myChoice: '' }] }),
  setMyChoice: (rollId, choice) => set({
    rolls: get().rolls.map((r) => r.id === rollId ? { ...r, myChoice: choice } : r),
  }),
  resolveRoll: (rollId, winnerName, winnerRoll) => set({
    rolls: get().rolls.map((r) => r.id === rollId ? { ...r, resolved: { winnerName, winnerRoll } } : r),
  }),
  dropRoll: (rollId) => set({ rolls: get().rolls.filter((r) => r.id !== rollId) }),
  reset: () => set({ active: false, dungeonId: '', mode: 'normal', status: 'waiting', bossId: '', phaseIdx: 0, mechanics: [], loot: [], rolls: [] }),
}));
